import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, FileTextIcon, GraduationCapIcon, MessageCircleIcon, PhoneIcon, PlusIcon, SendIcon, UsersIcon } from 'lucide-react';

// Partner application form (1Dash.Pro), embedded in the #apply section.
const APPLICATION_FORM_ID = 'rUAhi9infcpWTJGfYzpX';
const APPLICATION_FORM_SRC = `https://data.1dash.pro/widget/form/${APPLICATION_FORM_ID}`;
// Resize helper from the same form host. It grows the iframe to fit the form (mainly on phones).
const FORM_EMBED_SCRIPT = 'https://data.1dash.pro/js/form_embed.js';
const COMMUNITY_HREF = 'https://billing.adroimarketing.com/communities/groups/ad-roi-partners';

// TODO: the Partner New Deal form link (David is building it). Replace '#' with the form URL.
const SUBMIT_DEAL_HREF = '#';

// The site uses hash routing (#/page), so in-page anchors must scroll instead of changing the hash.
function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function ApplyButton({
  dark = false
}: {
  dark?: boolean;
}) {
  return <a href="#apply" onClick={e => scrollToSection(e, 'apply')} className={`group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-brand-red px-8 py-4 text-lg font-bold text-white shadow-lg transition-colors duration-150 ease-out hover:bg-brand-red-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${dark ? 'focus-visible:ring-white focus-visible:ring-offset-black' : 'focus-visible:ring-brand-red'}`} data-id="element-1240" data-mp="ls6sdx2copme">
      Apply to Become a Partner
      <ArrowRightIcon className="h-5 w-5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1241" data-mp="10ppq3ox0ad5z" />
    </a>;
}
const steps = [{
  title: 'Apply',
  body: 'Fill out the short form below. We will reach out to say hello and make sure it is a good fit.'
}, {
  title: 'Get trained in our partner community',
  body: 'Take our short training course and join other partners. Learn how to show our websites, marketing and monthly plans, and answer the questions owners ask.'
}, {
  title: 'Sell and get paid',
  body: 'Bring us the business owner. We do the work, keep it running and bill the client. You get paid every month.'
}];
const earnings = [{
  what: 'Website sale',
  earn: '30%',
  when: 'One time, on the website price'
}, {
  what: 'White Glove Onboarding ($997, with a plan)',
  earn: '30%',
  when: 'One time'
}, {
  what: 'Monthly plans (website, marketing and the 1Dash.Pro CRM)',
  earn: '20%',
  when: 'Every month the client pays'
}, {
  what: 'Recruit other partners',
  earn: '5%',
  when: 'Of what the partners you bring in sell'
}];
const perks = [{
  icon: GraduationCapIcon,
  title: 'Training course',
  body: 'Short lessons that show you what to say, what to show and how to close.'
}, {
  icon: PhoneIcon,
  title: 'Live demo line',
  body: 'Let owners try a Living Website themselves. They call (725) 715-5070 and watch vegastacotruck.com change live.',
  links: [{
    label: '(725) 715-5070',
    href: 'tel:17257155070'
  }, {
    label: 'vegastacotruck.com',
    href: 'https://vegastacotruck.com'
  }]
}, {
  icon: MessageCircleIcon,
  title: 'Partner Assistant',
  body: 'Quick answers about our services, plans, pricing and next steps, any time you need them.'
}, {
  icon: FileTextIcon,
  title: 'Partner agreement',
  body: 'A clear written agreement, so you know exactly what you earn and when.'
}, {
  icon: UsersIcon,
  title: 'Partner community',
  body: 'A group of partners sharing what works, plus direct access to our team.'
}];
const faqs = [{
  q: 'What can I sell?',
  a: 'Everything Ad ROI offers: websites (including Living Websites), marketing services like local SEO, ads, social media and reviews, and our monthly plans with the 1Dash.Pro CRM.'
}, {
  q: 'Do I need tech skills?',
  a: 'No. You do not build or run anything. If you can talk to a business owner about growing their business, you can do this. We handle the work and the tech.'
}, {
  q: 'Who does the work?',
  a: 'We do. Our team builds the websites, runs the marketing, keeps everything running and makes the changes the client asks for.'
}, {
  q: 'Who bills the client?',
  a: 'We do. The client pays Ad ROI directly. You never have to send an invoice or chase a payment.'
}, {
  q: 'When do I get paid?',
  a: 'Once a month, by the 15th. The minimum payout is $50.'
}, {
  q: 'Can I set my own website price?',
  a: 'Yes. The minimum website price is $1,000 unless we approve a lower price. You earn 30% of the website sale.'
}, {
  q: 'Do I keep earning if I stop selling?',
  a: 'Yes. You keep earning your monthly share for as long as your clients keep paying.'
}];
const sectionPad = 'px-[clamp(1rem,4vw,6rem)] py-20 lg:py-28';
const kicker = 'text-sm font-bold uppercase tracking-[0.14em] text-brand-red';
const h2 = 'mt-3 text-[clamp(2rem,3vw,3.5rem)] font-extrabold leading-tight tracking-tight text-brand-black';
const lead = 'text-[clamp(1.0625rem,1.2vw,1.375rem)] leading-relaxed text-gray-600';
const cardText = 'mb-8 mt-3 max-w-[56ch] text-[clamp(1rem,1.1vw,1.25rem)] leading-relaxed text-gray-600';
const cardButton = 'group mt-auto inline-flex items-center justify-center gap-2 self-start whitespace-nowrap rounded-md px-6 py-3.5 text-base font-bold transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2';
export function Partners() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Load the form resize helper once, so the embedded application grows to fit its content.
  useEffect(() => {
    if (document.getElementById('adroi-form-embed')) return;
    const script = document.createElement('script');
    script.id = 'adroi-form-embed';
    script.src = FORM_EMBED_SCRIPT;
    script.async = true;
    document.body.appendChild(script);
  }, []);
  return <>
      {/* HERO */}
      <section className="overflow-hidden bg-white px-[clamp(1rem,4vw,6rem)] pb-20 pt-14 lg:pb-28 lg:pt-20" data-id="element-1242" data-mp="1400nax4a0arm">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1243" data-mp="180kn68g8hshg">
          <p className={kicker} data-id="element-1244" data-mp="20nn79vk5y0o6">Ad ROI Partner Center</p>
          <h1 className="mt-5 max-w-[22ch] text-[clamp(2.5rem,4vw,5rem)] font-extrabold leading-[1.04] tracking-tight text-brand-black" data-id="element-1245" data-mp="1mxti1c4qxedx">
            Sell websites, marketing and monthly plans. <span className="text-brand-red" data-id="element-1246" data-mp="1qwsj2lncwr03">Get paid every month.</span>
          </h1>
          <p className={`mt-6 max-w-[64ch] ${lead}`} data-id="element-1247" data-mp="2emmvj23rkdjt">
            You bring the business owner. We do the work: websites, marketing and the monthly plans that keep it all
            running. We bill the client. You earn on the sale and every month the client stays.
          </p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row" data-id="element-1248" data-mp="30uryma76cyf">
            <ApplyButton data-id="element-1249" data-mp="hqjvup9ocv2e" />
            <a href="#/pricing" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border-2 border-gray-200 bg-white px-8 py-4 text-lg font-bold text-gray-700 transition-colors duration-150 ease-out hover:border-brand-black hover:text-brand-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-1250" data-mp="hoz417eqvq88">
              See what you would sell
            </a>
          </div>
          <p className="mt-6 text-sm font-medium text-brand-gray" data-id="element-1251" data-mp="3q527t22qkura">
            No tech skills needed. No building. No billing. No chasing payments.{' '}
            <a href="#partner-tools" onClick={e => scrollToSection(e, 'partner-tools')} className="font-bold text-brand-red underline underline-offset-4 transition-colors duration-150 ease-out hover:text-brand-red-dark" data-id="element-1252" data-mp="1agsesjc1bji1">
              Already a partner?
            </a>
          </p>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section aria-labelledby="partner-how" className={`bg-gray-50 ${sectionPad}`} data-id="element-1253" data-mp="3dnhoky3xqlk0">
        <p className={kicker} data-id="element-1254" data-mp="3dpaycm9uhlw8">How it works</p>
        <h2 id="partner-how" className={h2} data-id="element-1255" data-mp="3incfh1npnb52">
          Three simple steps.
        </h2>
        <ol className="relative mt-14 grid gap-12 md:grid-cols-3 md:gap-10 xl:gap-16" data-id="element-1256" data-mp="3mnmvl757vhxn">
          <span className="absolute left-6 right-6 top-6 hidden h-px bg-gray-200 md:block" aria-hidden="true" data-id="element-1257" data-mp="3rnirej3rg324" />
          {steps.map((step, i) => <li key={step.title} className="relative" data-id="element-1258" data-mp="gg1duzns2th" data-mpi={i}>
              <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-brand-red text-lg font-extrabold text-white ring-8 ring-gray-50" data-id="element-1259" data-mp="1x9rjpp4zrt9b">
                {i + 1}
              </span>
              <h3 className="mt-6 text-[clamp(1.375rem,1.6vw,1.875rem)] font-bold text-brand-black" data-id="element-1260" data-mp="1up1w9wb2x6cm">{step.title}</h3>
              <p className="mt-2 max-w-[46ch] text-[clamp(1rem,1.1vw,1.25rem)] leading-relaxed text-gray-600" data-id="element-1261" data-mp="1pxbjljoo66i1">{step.body}</p>
            </li>)}
        </ol>
      </section>

      {/* WHAT YOU EARN */}
      <section aria-labelledby="partner-earn" className={`bg-white ${sectionPad}`} data-id="element-1262" data-mp="1g1gv47xrlh7c">
        <p className={kicker} data-id="element-1263" data-mp="2dmmkr29xm2wp">What you earn</p>
        <h2 id="partner-earn" className={h2} data-id="element-1264" data-mp="2dlbjpa7vf99a">
          Paid on the sale. Paid every month after.
        </h2>

        {/* Table: tablet and up */}
        <div className="mt-12 hidden overflow-hidden rounded-xl border border-gray-200 md:block" data-id="element-1265" data-mp="23ogph3imrv0t">
          <table className="w-full text-left" data-id="element-1266" data-mp="1ypdiven05gqq">
            <thead className="bg-brand-black text-white" data-id="element-1267" data-mp="1ysri4e0dd9ty">
              <tr data-id="element-1268" data-mp="sb4du6gmq843">
                <th scope="col" className="px-6 py-4 text-sm font-bold uppercase tracking-[0.1em]" data-id="element-1269" data-mp="sbmkzy4ty6qs">
                  What you sell
                </th>
                <th scope="col" className="px-6 py-4 text-sm font-bold uppercase tracking-[0.1em]" data-id="element-1270" data-mp="1ty0vgd1gx8ko">
                  You earn
                </th>
                <th scope="col" className="px-6 py-4 text-sm font-bold uppercase tracking-[0.1em]" data-id="element-1271" data-mp="1p1iqpd651a8c">
                  How often
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-1272" data-mp="1ywatqwihopai">
              {earnings.map(row => <tr key={row.what} className="bg-white" data-id="element-1273" data-mp="2cnpbl00nukfz">
                  <th scope="row" className="px-6 py-5 text-[clamp(1rem,1.1vw,1.25rem)] font-semibold text-brand-black" data-id="element-1274" data-mp="28o8646l8hnat">
                    {row.what}
                  </th>
                  <td className="px-6 py-5 text-[clamp(1.25rem,1.5vw,1.75rem)] font-extrabold text-brand-red" data-id="element-1275" data-mp="2hodcqeilshnx">{row.earn}</td>
                  <td className="px-6 py-5 text-[clamp(1rem,1.1vw,1.25rem)] text-gray-600" data-id="element-1276" data-mp="s8x3z08lcxyk">{row.when}</td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Cards: phone */}
        <ul className="mt-10 space-y-4 md:hidden" data-id="element-1277" data-mp="2yyqrqguwaov5">
          {earnings.map(row => <li key={row.what} className="rounded-xl border border-gray-200 p-5" data-id="element-1278" data-mp="33x21krl6wzdd">
              <p className="font-semibold text-brand-black" data-id="element-1279" data-mp="2p8fjwj83bviz">{row.what}</p>
              <p className="mt-2 text-3xl font-extrabold text-brand-red" data-id="element-1280" data-mp="2p61o285l6fad">{row.earn}</p>
              <p className="mt-1 text-sm text-gray-600" data-id="element-1281" data-mp="2u4w6va3k03uh">{row.when}</p>
            </li>)}
        </ul>

        <p className="mt-6 rounded-lg bg-brand-tint px-5 py-4 text-base font-medium text-brand-black" data-id="element-1282" data-mp="2g9kayzdnw7bz">
          Minimum website price $1,000. Payouts monthly by the 15th ($50 minimum).
        </p>
      </section>

      {/* EXAMPLE */}
      <section aria-labelledby="partner-example" className={`bg-brand-black ${sectionPad}`} data-id="element-1283" data-mp="2b7bjk7gftdbz">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-white/70" data-id="element-1284" data-mp="4hf38bxi5c1t">Example</p>
        <h2 id="partner-example" className="mt-3 text-[clamp(2rem,3vw,3.5rem)] font-extrabold leading-tight tracking-tight text-white" data-id="element-1285" data-mp="9h9s69xpvrk8">
          What one client can look like.
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2" data-id="element-1286" data-mp="30rhplt0zfnd">
          <div className="rounded-xl border border-white/15 bg-white/5 p-8" data-id="element-1287" data-mp="2z6obzig62r3">
            <p className="text-[clamp(1.0625rem,1.2vw,1.375rem)] text-gray-300" data-id="element-1288" data-mp="3u7kk2szk1kth">A $1,500 website</p>
            <p className="mt-2 text-[clamp(2.75rem,4vw,4.5rem)] font-extrabold leading-none text-white" data-id="element-1289" data-mp="3q6iqossbk2zf">= $450</p>
            <p className="mt-3 text-sm text-gray-400" data-id="element-1290" data-mp="qpar64nt9yh1">One time, on the website sale.</p>
          </div>
          <div className="rounded-xl border border-white/15 bg-white/5 p-8" data-id="element-1291" data-mp="lrrbr1fdnfni">
            <p className="text-[clamp(1.0625rem,1.2vw,1.375rem)] text-gray-300" data-id="element-1292" data-mp="gyy7eentm9by">A Pilot client</p>
            <p className="mt-2 text-[clamp(2.75rem,4vw,4.5rem)] font-extrabold leading-none text-white" data-id="element-1293" data-mp="cxw67sacfn7c">= $123</p>
            <p className="mt-3 text-sm text-gray-400" data-id="element-1294" data-mp="19i4z4en0oqbr">Every month they stay.</p>
          </div>
        </div>
        <p className="mt-8 text-base text-gray-400" data-id="element-1295" data-mp="14ok7d4c0osl0">
          These are examples, not promises. What you earn depends on what you sell.
        </p>
      </section>

      {/* WHAT YOU GET */}
      <section aria-labelledby="partner-get" className={`bg-white ${sectionPad}`} data-id="element-1296" data-mp="2sbyb434rv69w">
        <p className={kicker} data-id="element-1297" data-mp="2wci8n7t9cbwk">What you get</p>
        <h2 id="partner-get" className={h2} data-id="element-1298" data-mp="31ccxl5th2rez">
          Everything you need to sell with confidence.
        </h2>
        <ul className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-5" data-id="element-1299" data-mp="36bphpisylkdv">
          {perks.map(perk => <li key={perk.title} className="rounded-xl border border-gray-200 p-6" data-id="element-1300" data-mp="3b999llikxvoa">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-tint text-brand-red" data-id="element-1301" data-mp="3g10qt5fuip1r">
                <perk.icon className="h-5 w-5" aria-hidden="true" data-id="element-1302" data-mp="3k1kny90f5snw" />
              </span>
              <h3 className="mt-5 text-xl font-bold text-brand-black" data-id="element-1303" data-mp="3p309e26ie16t">{perk.title}</h3>
              <p className="mt-2 leading-relaxed text-gray-600" data-id="element-1304" data-mp="3u08sq63e8b9n">{perk.body}</p>
              {perk.links && <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2" data-id="element-1305" data-mp="3gd9sauvjrucv">
                  {perk.links.map(link => <a key={link.href} href={link.href} target={link.href.startsWith('http') ? '_blank' : undefined} rel={link.href.startsWith('http') ? 'noreferrer' : undefined} className="text-sm font-bold text-brand-red underline underline-offset-4 transition-colors duration-150 ease-out hover:text-brand-red-dark" data-id="element-1306" data-mp="3bep09iqg8gzi">
                      {link.label}
                    </a>)}
                </div>}
            </li>)}
        </ul>
      </section>

      {/* FAQ */}
      <section aria-labelledby="partner-faq" className={`bg-gray-50 ${sectionPad}`} data-id="element-1307" data-mp="mne1j0esngn4">
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: f.a
            }
          }))
        })
      }} data-id="element-1308" data-mp="mp8p6mhg9muw" />
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16" data-id="element-1309" data-mp="g8ysrtkmamat">
          <div data-id="element-1310" data-mp="k997bumh6l1a">
            <p className={kicker} data-id="element-1311" data-mp="kats4sizmply">Questions</p>
            <h2 id="partner-faq" className={h2} data-id="element-1312" data-mp="17y1w974ojsme">
              Partner FAQ
            </h2>
          </div>
          <div className="divide-y divide-gray-200 border-y border-gray-200" data-id="element-1313" data-mp="1cxoddrnmpt0h">
            {faqs.map((faq, i) => {
            const isOpen = openFaq === i;
            return <div key={faq.q} data-id="element-1314" data-mp="1373cfxkhgcee" data-mpi={i}>
                  <h3 data-id="element-1315" data-mp="136225vbajtxq">
                    <button type="button" id={`partner-faq-q-${i}`} aria-expanded={isOpen} aria-controls={`partner-faq-a-${i}`} onClick={() => setOpenFaq(isOpen ? null : i)} className="flex min-h-[64px] w-full items-center justify-between gap-6 py-5 text-left text-lg font-bold text-brand-black transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:text-brand-red" data-id="element-1316" data-mp="135iioa5zsxhl">
                      {faq.q}
                      <PlusIcon className={`h-5 w-5 flex-shrink-0 text-brand-red transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-1317" data-mp="1rjvgmwv7xryy" />
                    </button>
                  </h3>
                  <AnimatePresence initial={false} data-id="element-1318" data-mp="1dsymh1ln92ps">
                    {isOpen && <motion.div id={`partner-faq-a-${i}`} role="region" aria-labelledby={`partner-faq-q-${i}`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-1319" data-mp="18v6wwe8h1sor">
                        <p className="max-w-[75ch] pb-6 pr-10 text-base leading-relaxed text-gray-600" data-id="element-1320" data-mp="ynuk9zuvrsy3">{faq.a}</p>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* APPLY: embedded partner application form */}
      <section id="apply" aria-labelledby="partner-apply" className={`scroll-mt-24 bg-white ${sectionPad}`} data-id="element-1321" data-mp="fy64jdzloj3b">
        <p className={kicker} data-id="element-1322" data-mp="kqpgk6in3660">Apply</p>
        <h2 id="partner-apply" className={h2} data-id="element-1323" data-mp="krqvim2s0ct0">
          Apply to Become a Partner
        </h2>
        <p className={`mt-5 max-w-[64ch] ${lead}`} data-id="element-1324" data-mp="3je3gnshbo14a">
          Tell us a little about you. We will reach out, walk you through the training and the demo line, and help you
          get ready for your first conversation.
        </p>
        <div className="mt-12 w-full" data-id="element-1325" data-mp="12ezuavnalsdi">
          <iframe src={APPLICATION_FORM_SRC} id={`inline-${APPLICATION_FORM_ID}`} title="Ad ROI Partner Application" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-activation-type="alwaysActivated" data-deactivation-type="neverDeactivate" data-form-name="Ad ROI Partner Application" data-form-id={APPLICATION_FORM_ID} className="block min-h-[1300px] w-full border-0 md:min-h-[1100px]" style={{
          width: '100%',
          border: 'none'
        }} data-id="element-1326" data-mp="12g2nja8386wl" />
        </div>
        <p className="mt-6 text-gray-600" data-id="element-1327" data-mp="1u67btjyyxe33">
          Questions first?{' '}
          <a href="#/contact" className="font-semibold text-brand-red underline underline-offset-4 transition-colors duration-150 ease-out hover:text-brand-red-dark focus:outline-none focus-visible:text-brand-red-dark" data-id="element-1328" data-mp="293zrqd8fg4ht">
            Contact us
          </a>
        </p>
      </section>

      {/* ALREADY A PARTNER */}
      <section id="partner-tools" aria-labelledby="partner-tools-title" className={`scroll-mt-24 bg-brand-black ${sectionPad}`} data-id="element-1329" data-mp="2row42yu0uyu4">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-white/70" data-id="element-1330" data-mp="2iq0xhln2fmqa">Partners</p>
        <h2 id="partner-tools-title" className="mt-3 text-[clamp(2rem,3vw,3.5rem)] font-extrabold leading-tight tracking-tight text-white" data-id="element-1331" data-mp="2414i13ioj7fn">
          Already a partner?
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2" data-id="element-1332" data-mp="1v0y6hn6jkhj4">
          <div className="flex flex-col rounded-xl bg-white p-8 lg:p-10" data-id="element-1333" data-mp="1v2j1fdwrq2hi">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-brand-tint text-brand-red" data-id="element-1334" data-mp="20144dl3en09n">
              <UsersIcon className="h-6 w-6" aria-hidden="true" data-id="element-1335" data-mp="1l3brz1t5mrzo" />
            </span>
            <h3 className="mt-6 text-[clamp(1.5rem,1.8vw,2.125rem)] font-extrabold text-brand-black" data-id="element-1336" data-mp="1q0tsc3t3vyw2">Partner Community</h3>
            <p className={cardText} data-id="element-1337" data-mp="1p4wq18gxtf5n">
              Your training, the course, sales scripts, the partner agreement and your Partner Assistant, all in one place.
            </p>
            <a href={COMMUNITY_HREF} target="_blank" rel="noreferrer" className={`${cardButton} bg-brand-red text-white hover:bg-brand-red-dark`} data-id="element-1338" data-mp="1f9d4ng001yv5">
              Go to the Partner Community
              <ArrowRightIcon className="h-5 w-5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1339" data-mp="2cw2bc35ncznc" />
            </a>
          </div>
          <div className="flex flex-col rounded-xl bg-white p-8 lg:p-10" data-id="element-1340" data-mp="1y538l8rht5bo">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-brand-tint text-brand-red" data-id="element-1341" data-mp="234xx55nspitk">
              <SendIcon className="h-6 w-6" aria-hidden="true" data-id="element-1342" data-mp="2quiitwbc2u6j" />
            </span>
            <h3 className="mt-6 text-[clamp(1.5rem,1.8vw,2.125rem)] font-extrabold text-brand-black" data-id="element-1343" data-mp="2vnbnkk6sy2im">Submit a Deal</h3>
            <p className={cardText} data-id="element-1344" data-mp="k5qd544gqq16">Closed a sale? Send it to us for approval and we'll invoice the client.</p>
            {/* TODO: the Partner New Deal form link (David is building it). Set SUBMIT_DEAL_HREF at the top of this file. */}
            <a href={SUBMIT_DEAL_HREF} onClick={e => {
            if (SUBMIT_DEAL_HREF === '#') e.preventDefault();
          }} className={`${cardButton} border-2 border-brand-black bg-white text-brand-black hover:bg-brand-black hover:text-white`} data-id="element-1345" data-mp="oyjgpoo73sbo">
              Submit a Deal
              <ArrowRightIcon className="h-5 w-5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1346" data-mp="aaydlurblt3r" />
            </a>
          </div>
        </div>
      </section>
    </>;
}